import { reviewsStyles, SCORE_BADGE_SVG } from './reviewsStyles.js';
import { getTranslation } from './utils/translations.js';

/**
 * Kiyoh Reviews Widget - Full reviews display with AI summary
 */
class KiyohReviewsWidget extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.state = {
      locationId: null,
      apiBaseUrl: null,
      language: 'nl',
      productId: null,
      loading: true,
      error: null,
      summary: null,
      reviews: [],
      pagination: { page: 1, limit: 10, total: 0, pages: 0 },
      filters: { rating: null, topic: null },
      // Q&A state
      qaQuestion: '',
      qaAnswer: null,
      qaLoading: false,
      qaError: null,
      qaPopular: [],
      // Expand/collapse
      expanded: false
    };
  }

  static get observedAttributes() {
    return ['data-location-id', 'data-api-base-url', 'data-product-id', 'data-language'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-location-id') this.state.locationId = newValue;
    if (name === 'data-api-base-url') this.state.apiBaseUrl = newValue;
    if (name === 'data-product-id') this.state.productId = newValue;
    if (name === 'data-language') this.state.language = newValue || 'nl';
  }

  async connectedCallback() {
    this.render();
    await this.loadReviews();
  }

  async loadReviews() {
    if (!this.state.locationId) {
      this.setState({ error: 'data-location-id is required', loading: false });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const baseUrl = this.state.apiBaseUrl || 'https://your-api-domain.com/api/v1';
      const { page, limit } = this.state.pagination;
      const { rating, topic } = this.state.filters;

      let url = `${baseUrl}/reviews/${this.state.locationId}?page=${page}&limit=${limit}&lang=${this.state.language}`;
      if (this.state.productId) url += `&productId=${encodeURIComponent(this.state.productId)}`;
      if (rating) url += `&rating=${rating}`;
      if (topic) url += `&topic=${encodeURIComponent(topic)}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to load reviews');

      this.setState({
        loading: false,
        summary: data.data.summary,
        reviews: data.data.reviews,
        pagination: data.data.pagination
      });
      // Load popular Q&A questions
      await this.loadPopularQuestions();
    } catch (error) {
      console.error('[KiyohReviewsWidget] Error:', error);
      this.setState({ loading: false, error: error.message });
    }
  }

  setState(updates) {
    this.state = { ...this.state, ...updates };
    // Don't re-render the full widget if we're expanded and only reviews/pagination changed
    // (would reset scroll position and close drawer)
    const reviewsOnlyKeys = ['reviews', 'pagination', 'loading', 'error'];
    const keys = Object.keys(updates);
    const isReviewsOnly = keys.every(k => reviewsOnlyKeys.includes(k));
    if (this.state.expanded && isReviewsOnly) {
      this._updateReviewsList();
    } else {
      this.render();
    }
  }

  _updateReviewsList() {
    // Swap out just the review cards + pagination inside the drawer
    const drawerBody = this.shadowRoot.querySelector('.drawer-body');
    if (!drawerBody) { this.render(); return; }

    const { reviews, pagination, filters, loading } = this.state;
    const lang = this.state.language === 'fr' ? 'fr-FR' : this.state.language === 'en' ? 'en-GB' : 'nl-NL';

    // Find or create a reviews-list container
    let listEl = drawerBody.querySelector('.reviews-list-container');
    if (!listEl) { this.render(); return; }

    if (loading) {
      listEl.innerHTML = `<div class="loading"><div class="spinner"></div><span>${this.t('loading')}</span></div>`;
      return;
    }

    listEl.innerHTML = reviews.length === 0
      ? `<div class="empty-msg">${this.t('noReviews')}</div>`
      : reviews.map(r => {
          const stars = this.renderStars(r.rating);
          const dateStr = r.date ? new Date(r.date).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
          return `
            <div class="review-card">
              <div class="review-top-row">
                <div class="review-score-circle ${this.getScoreClass(r.rating)}">${r.rating}</div>
                <div style="flex:1">
                  <div class="review-stars">${stars}</div>
                  ${r.title ? `<div class="review-title">${r.title}</div>` : ''}
                </div>
              </div>
              ${r.text ? `<div class="review-text">${r.text}</div>` : ''}
              <div class="verified-badge"><i class="fa-solid fa-shield-halved"></i> ${this.t('verifiedReviews')}</div>
              <hr class="review-divider">
              <div class="review-meta-row">
                ${dateStr ? `<span><i class="fa-regular fa-calendar"></i> ${dateStr}</span>` : ''}
                ${r.recommend ? `<span style="color:var(--green)"><i class="fa-solid fa-thumbs-up"></i></span>` : ''}
              </div>
              <div class="review-author">
                <strong>${r.author || this.t('anonymous')}</strong>
                ${r.city ? `<br><span>${r.city}</span>` : ''}
              </div>
              ${r.shopReply ? `
                <div class="shop-reply">
                  <div class="shop-reply-label"><i class="fa-solid fa-comment"></i> ${this.t('shopReply')}:</div>
                  ${r.shopReply}
                </div>` : ''}
            </div>`;
        }).join('');

    // Update pagination
    let pagEl = drawerBody.querySelector('.pagination');
    if (pagination.pages > 1) {
      const pagHtml = `
        <div class="pagination">
          <button class="page-btn" ${pagination.page === 1 ? 'disabled' : ''} data-page="${pagination.page - 1}">‹</button>
          ${Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
            const p = i + 1;
            return `<button class="page-btn ${pagination.page === p ? 'active' : ''}" data-page="${p}">${p}</button>`;
          }).join('')}
          ${pagination.pages > 5 ? `<span style="padding:0 4px">…</span><button class="page-btn" data-page="${pagination.pages}">${pagination.pages}</button>` : ''}
          <button class="page-btn" ${pagination.page === pagination.pages ? 'disabled' : ''} data-page="${pagination.page + 1}">›</button>
        </div>`;
      if (pagEl) pagEl.outerHTML = pagHtml;
      else listEl.insertAdjacentHTML('afterend', pagHtml);
    } else if (pagEl) pagEl.remove();

    // Re-attach pagination listeners
    drawerBody.querySelectorAll('.page-btn[data-page]').forEach(btn =>
      btn.addEventListener('click', () => {
        const p = parseInt(btn.dataset.page);
        if (p >= 1 && p <= pagination.pages) this.handlePageChange(p);
      }));
  }

  // Shorthand translation helper
  t(key) {
    return getTranslation(this.state.language, key);
  }

  handleFilterChange(type, value) {
    this.state.filters[type] = value || null;
    this.state.pagination.page = 1;
    this.loadReviews();
  }

  handleTopicClick(topic) {
    const current = this.state.filters.topic;
    this.handleFilterChange('topic', current === topic ? null : topic);
  }

  handlePageChange(page) {
    this.state.pagination.page = page;
    this.loadReviews();
  }

  resetFilters() {
    this.state.filters = { rating: null, topic: null };
    this.state.pagination.page = 1;
    this.loadReviews();
  }

  async loadPopularQuestions() {
    try {
      const baseUrl = this.state.apiBaseUrl || 'https://your-api-domain.com/api/v1';
      const res = await fetch(`${baseUrl}/qa/popular/${this.state.locationId}?limit=3`);
      const data = await res.json();
      if (data.success && data.data.questions.length > 0) {
        this.state.qaPopular = data.data.questions;
      } else {
        this.state.qaPopular = [
          { question: this.t('popularQ1') },
          { question: this.t('popularQ2') },
          { question: this.t('popularQ3') }
        ];
      }
    } catch (_) {
      this.state.qaPopular = [];
    }
  }

  async submitQuestion(question) {
    if (!question.trim()) return;
    const productId = this.getAttribute('data-product-id') || this.state.productId;
    this.state.qaLoading = true;
    this.state.qaAnswer = null;
    this.state.qaError = null;
    this._updateQASection(); // partial update — no full re-render

    try {
      const baseUrl = this.state.apiBaseUrl || 'https://your-api-domain.com/api/v1';
      const res = await fetch(`${baseUrl}/qa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locationId: this.state.locationId,
          productCode: productId,
          question,
          language: this.state.language
        })
      });
      const data = await res.json();
      if (data.success) {
        this.state.qaAnswer = data.data;
      } else {
        this.state.qaError = data.error || 'Failed to get answer';
      }
    } catch (e) {
      this.state.qaError = e.message;
    }
    this.state.qaLoading = false;
    this._updateQASection(); // partial update — no full re-render
  }

  _updateQASection() {
    const qaCard = this.shadowRoot.querySelector('.qa-card');
    if (!qaCard) { this.render(); return; } // fallback if not found

    // Only update the dynamic parts inside the qa-card
    const submitBtn = qaCard.querySelector('#qa-submit');
    const input = qaCard.querySelector('#qa-input');
    const errorEl = qaCard.querySelector('.qa-error');
    const answerEl = qaCard.querySelector('.qa-answer');

    // Update button state
    if (submitBtn) {
      submitBtn.disabled = this.state.qaLoading;
      submitBtn.innerHTML = this.state.qaLoading
        ? '<span class="qa-spinner"></span>'
        : this.t('qaButton');
    }
    if (input) input.disabled = this.state.qaLoading;

    // Update error
    if (this.state.qaError) {
      if (errorEl) {
        errorEl.textContent = '⚠️ ' + this.state.qaError;
      } else {
        const div = document.createElement('div');
        div.className = 'qa-error';
        div.textContent = '⚠️ ' + this.state.qaError;
        qaCard.querySelector('.qa-input-row').insertAdjacentElement('afterend', div);
      }
    } else if (errorEl) {
      errorEl.remove();
    }

    // Update answer
    if (this.state.qaAnswer) {
      if (answerEl) {
        answerEl.querySelector('.qa-answer-label').innerHTML =
          `<i class="fa-solid fa-robot"></i> ${this.t('qaAnswerLabel')}`;
        answerEl.lastChild.textContent = this.state.qaAnswer.answer;
      } else {
        const div = document.createElement('div');
        div.className = 'qa-answer';
        div.innerHTML = `
          <div class="qa-answer-label"><i class="fa-solid fa-robot"></i> ${this.t('qaAnswerLabel')}</div>
          <div>${this.state.qaAnswer.answer}</div>`;
        qaCard.appendChild(div);
      }
    } else if (answerEl) {
      answerEl.remove();
    }
  }

  renderStars(rating) {
    // rating is out of 10, stars out of 5
    const score = rating / 2;
    const full = Math.floor(score);
    const half = score % 1 >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);
    const s = '<i class="fa-solid fa-star"></i>';
    const h = '<i class="fa-solid fa-star-half-stroke"></i>';
    const e = '<i class="fa-regular fa-star"></i>';
    return s.repeat(full) + (half ? h : '') + e.repeat(empty);
  }

  getScoreClass(rating) {
    if (rating >= 8) return '';      // green (default)
    if (rating >= 6) return 'mid';   // yellow
    return 'low';                    // red
  }

  render() {
    const { loading, error, summary, reviews, pagination, filters } = this.state;
    const lang = this.state.language === 'fr' ? 'fr-FR' : this.state.language === 'en' ? 'en-GB' : 'nl-NL';

    // FA CDN link — injected into shadow DOM
    const faLink = `<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">`;
    const interLink = `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap">`;

    // ── Collapsed bar ──
    if (!this.state.expanded) {
      const pros = summary?.aiSummary?.pros?.slice(0, 3) || [];
      this.shadowRoot.innerHTML = `
        ${faLink}${interLink}
        <style>${reviewsStyles}</style>
        <div class="collapsed-bar" id="expand-btn">
          <div class="collapsed-left">
            <div class="collapsed-badge">
              <div class="collapsed-badge-svg">${SCORE_BADGE_SVG}</div>
              <span class="collapsed-score">${summary ? summary.averageRating.toFixed(1) : '…'}</span>
            </div>
            <div class="collapsed-info">
              <div class="collapsed-count">${summary ? this.t('collapsedReviews').replace('{count}', summary.reviewCount.toLocaleString()) : this.t('collapsedLoading')}</div>
              <div class="collapsed-pros">
                ${pros.map(p => `<span class="collapsed-pro-chip"><i class="fa-solid fa-check"></i> ${p}</span>`).join('')}
                ${!pros.length && summary ? `<span class="collapsed-pro-chip"><i class="fa-solid fa-star"></i> ${this.t('positivePercent').replace('{pct}', summary.positiveSentimentPct)}</span>` : ''}
              </div>
            </div>
          </div>
          <div class="collapsed-right">
            <span class="collapsed-cta">${this.t('readReviews')} <i class="fa-solid fa-chevron-up"></i></span>
          </div>
        </div>
      `;
      this.shadowRoot.getElementById('expand-btn').addEventListener('click', () => {
        this.state.expanded = true;
        this.render();
      });
      return;
    }

    this.shadowRoot.innerHTML = `
      ${faLink}
      ${interLink}
      <style>${reviewsStyles}</style>
      <div class="drawer-overlay" id="drawer-overlay">
        <div class="drawer">
          <div class="drawer-header">
            <div class="drawer-title">${this.t('drawerTitle')}</div>
            <button class="drawer-close" id="drawer-close"><i class="fa-solid fa-xmark"></i></button>
          </div>
          <div class="drawer-body">
            <div class="widget">

        ${loading ? `
          <div class="loading"><div class="spinner"></div><span>${this.t('loading')}</span></div>
        ` : error ? `
          <div class="error-msg">⚠️ ${error}</div>
        ` : `

          <!-- ── Review overzicht ── -->
          <div class="card">
            <div class="overview-title">${this.t('reviewOverview')}</div>
            <div class="sentiment-row">
              <span>${this.t('positiveSentiment')}</span>
              <div class="sentiment-bar-wrap">
                <div class="sentiment-bar-fill" style="width:${summary ? summary.positiveSentimentPct : 0}%"></div>
              </div>
              <span class="sentiment-pct">${summary ? summary.positiveSentimentPct : 0}%</span>
            </div>
            <div class="overview-text">
              ${summary && summary.aiSummary && summary.aiSummary.summary
                ? summary.aiSummary.summary
                : summary ? this.t('summaryText').replace('{rating}', summary.averageRating.toFixed(1)).replace('{pct}', summary.positiveSentimentPct) : ''}
            </div>
            ${summary && summary.aiSummary && summary.aiSummary.pros && summary.aiSummary.pros.length ? `
              <div class="pros-label"><i class="fa-solid fa-arrow-trend-up"></i> ${this.t('strongPoints')}</div>
              ${summary.aiSummary.pros.map(p => `<div class="pro-item">${p}</div>`).join('')}
            ` : ''}
            <div class="ai-source">
              <i class="fa-solid fa-robot"></i>
              ${this.t('basedOn')} ${summary ? summary.reviewCount.toLocaleString() : 0} ${this.t('reviews')}
            </div>
          </div>

          <!-- ── Score badge + distribution ── -->
          <div class="card">
            <div class="score-dist-row">
              <div class="score-badge-wrap">
                <div class="score-badge-svg">
                  ${SCORE_BADGE_SVG}
                  <div class="score-badge-number">${summary ? summary.averageRating.toFixed(1) : '0'}</div>
                </div>
                <div class="badge-stars">${summary ? this.renderStars(summary.averageRating) : ''}</div>
                <div class="badge-count">${summary ? summary.reviewCount.toLocaleString() : 0} ${this.t('inLast12Months')}</div>
              </div>
              <div class="dist-list">
                ${summary && summary.distribution ? summary.distribution.map(d => `
                  <div class="dist-row" data-rating="${d.score}">
                    <span class="dist-score">${d.score}</span>
                    <div class="dist-bar-wrap">
                      <div class="dist-bar-fill" style="width:${summary.reviewCount ? (d.count / summary.reviewCount * 100).toFixed(1) : 0}%"></div>
                    </div>
                    <span class="dist-count">${d.count}</span>
                  </div>`).join('') : ''}
              </div>
            </div>
          </div>

          <!-- ── Topics + Q&A combined card ── -->
          <div class="qa-card">
            <div class="qa-header">
              <div class="qa-title">${this.t('qaTitle')}</div>
              ${summary ? `
                <div class="qa-score-badge">
                  <span class="qa-score-stars">${this.renderStars(summary.averageRating)}</span>
                  <span class="qa-score-num">${summary.averageRating.toFixed(1)}/10</span>
                  <span class="qa-score-count">(${summary.reviewCount.toLocaleString()})</span>
                  <span class="qa-score-check">✓</span>
                </div>` : ''}
            </div>

            ${summary && (summary.aiSummary?.pros?.length || summary.topics?.length) ? `
              <div style="margin-bottom:12px">
                <div style="font-weight:700;font-size:.88rem;margin-bottom:2px">${this.t('chooseImportant')}</div>
                <div style="font-size:.75rem;color:var(--text-muted);margin-bottom:8px">${this.t('chooseImportantSub')}</div>
                <div class="qa-chips">
                  ${(summary.aiSummary?.tags?.length
                    ? summary.aiSummary.tags
                    : summary.aiSummary?.pros?.length
                      ? summary.aiSummary.pros.map(p => ({ label: p }))
                      : summary.topics
                  ).map((t, i) => {
                    const label = typeof t === 'string' ? t : t.label;
                    const count = t.count || null;
                    return `
                    <button class="qa-chip topic-chip ${filters.topic === label ? 'active' : ''}" data-topic="${label}" data-qa-topic="${i}">
                      ${label}${count ? ` <span class="topic-count">${count}</span>` : ''}
                    </button>`;
                  }).join('')}
                </div>
              </div>
              <hr style="border:none;border-top:1px solid var(--border);margin-bottom:12px">` : ''}

            ${this.state.qaPopular.length > 0 && this.state.qaPopular.every(q => {
              // Only show popular questions if they match the current language
              // Simple heuristic: check for Dutch-only words
              const nlWords = /\b(je|dit|het|een|van|de|wat|hoe|is|zijn|voor|met)\b/i;
              return !nlWords.test(q.question) || this.state.language === 'nl';
            }) ? `
              <div class="qa-chips">
                ${this.state.qaPopular.map((q, i) => `
                  <button class="qa-chip" data-qa-popular="${i}">
                    ${q.question.length > 40 ? q.question.substring(0, 40) + '...' : q.question}
                  </button>`).join('')}
              </div>` : ''}

            <div class="qa-input-row">
              <input class="qa-input" id="qa-input" type="text"
                placeholder="${this.t('qaPlaceholder')}"
                maxlength="200" ${this.state.qaLoading ? 'disabled' : ''}
                value="${this.state.qaQuestion ? this.state.qaQuestion.replace(/"/g, '&quot;') : ''}"/>
              <button class="qa-btn" id="qa-submit" ${this.state.qaLoading ? 'disabled' : ''}>
                ${this.state.qaLoading
                  ? `<span class="qa-spinner"></span>`
                  : this.t('qaButton')}
              </button>
            </div>

            ${this.state.qaError ? `<div class="qa-error">⚠️ ${this.state.qaError}</div>` : ''}

            ${this.state.qaAnswer ? `
              <div class="qa-answer">
                <div class="qa-answer-label">
                  <i class="fa-solid fa-robot"></i> ${this.t('qaAnswerLabel')}
                </div>
                ${this.state.qaAnswer.answer}
              </div>` : ''}
          </div>

          <!-- ── Filters ── -->
          <div class="filters-bar">
            <span class="filters-label">${this.t('filters')}</span>
            <span class="filter-stars">
              <i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i>
            </span>
            <select class="filter-select" data-filter="rating">
              <option value="">${this.t('showAllScores')}</option>
              ${[10,9,8,7,6,5,4,3,2,1].map(s => `
                <option value="${s}" ${filters.rating == s ? 'selected' : ''}>${s} ${s === 1 ? this.t('star') : this.t('stars')}</option>
              `).join('')}
            </select>
            <button class="reset-btn" title="Reset"><i class="fa-solid fa-rotate-right"></i></button>
          </div>

          <!-- ── Reviews ── -->
          <div class="reviews-list-container">
          ${reviews.length === 0 ? `<div class="empty-msg">${this.t('noReviews')}</div>` : `
            ${reviews.map(r => {
              const stars = this.renderStars(r.rating);
              const dateStr = r.date ? new Date(r.date).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
              return `
                <div class="review-card">
                  <div class="review-top-row">
                    <div class="review-score-circle ${this.getScoreClass(r.rating)}">${r.rating}</div>
                    <div style="flex:1">
                      <div class="review-stars">${stars}</div>
                      ${r.title ? `<div class="review-title">${r.title}</div>` : ''}
                    </div>
                  </div>
                  ${r.text ? `<div class="review-text">${r.text}</div>` : ''}
                  <div class="verified-badge">
                    <i class="fa-solid fa-shield-halved"></i> ${this.t('verifiedReviews')}
                  </div>
                  <hr class="review-divider">
                  <div class="review-meta-row">
                    ${dateStr ? `<span><i class="fa-regular fa-calendar"></i> ${dateStr}</span>` : ''}
                    ${r.recommend ? `<span style="color:var(--green)"><i class="fa-solid fa-thumbs-up"></i></span>` : ''}
                  </div>
                  <div class="review-author">
                    <strong>${r.author || this.t('anonymous')}</strong>
                    ${r.city ? `<br><span>${r.city}</span>` : ''}
                  </div>
                  ${r.shopReply ? `
                    <div class="shop-reply">
                      <div class="shop-reply-label">
                        <i class="fa-solid fa-comment"></i> ${this.t('shopReply')}:
                      </div>
                      ${r.shopReply}
                    </div>` : ''}
                </div>`;
            }).join('')}
          `}
          </div><!-- reviews-list-container -->

          <!-- ── Pagination ── -->
          ${pagination.pages > 1 ? `
            <div class="pagination">
              <button class="page-btn" ${pagination.page === 1 ? 'disabled' : ''} data-page="${pagination.page - 1}">‹</button>
              ${Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                const p = i + 1;
                return `<button class="page-btn ${pagination.page === p ? 'active' : ''}" data-page="${p}">${p}</button>`;
              }).join('')}
              ${pagination.pages > 5 ? `<span style="padding:0 4px">…</span><button class="page-btn" data-page="${pagination.pages}">${pagination.pages}</button>` : ''}
              <button class="page-btn" ${pagination.page === pagination.pages ? 'disabled' : ''} data-page="${pagination.page + 1}">›</button>
            </div>
          ` : ''}

        `}
      </div>
          </div><!-- drawer-body -->
        </div><!-- drawer -->
      </div><!-- drawer-overlay -->
    `;

    // Close drawer
    const drawerClose = this.shadowRoot.getElementById('drawer-close');
    const drawerOverlay = this.shadowRoot.getElementById('drawer-overlay');
    if (drawerClose) drawerClose.addEventListener('click', () => { this.state.expanded = false; this.render(); });
    if (drawerOverlay) drawerOverlay.addEventListener('click', e => {
      if (e.target === drawerOverlay) { this.state.expanded = false; this.render(); }
    });

    if (!loading && !error) {
      // Q&A
      const qaInput = this.shadowRoot.querySelector('#qa-input');
      const qaSubmit = this.shadowRoot.querySelector('#qa-submit');
      if (qaSubmit) {
        qaSubmit.addEventListener('click', () => {
          const q = this.shadowRoot.querySelector('#qa-input')?.value?.trim();
          if (q) { this.state.qaQuestion = q; this.submitQuestion(q); }
        });
      }
      if (qaInput) {
        qaInput.addEventListener('keydown', e => {
          if (e.key === 'Enter') {
            const q = e.target.value.trim();
            if (q) { this.state.qaQuestion = q; this.submitQuestion(q); }
          }
        });
      }
      this.shadowRoot.querySelectorAll('[data-qa-popular]').forEach(btn => {
        btn.addEventListener('click', () => {
          const q = this.state.qaPopular[parseInt(btn.dataset.qaPopular)]?.question;
          if (q) { this.state.qaQuestion = q; this.submitQuestion(q); }
        });
      });

      // Topics
      this.shadowRoot.querySelectorAll('[data-qa-topic]').forEach(btn =>
        btn.addEventListener('click', () => {
          const topic = btn.dataset.topic;
          // Toggle topic filter
          this.handleTopicClick(topic);
          // Also pre-fill the question input
          const input = this.shadowRoot.querySelector('#qa-input');
          if (input && !input.value) input.value = topic + '?';
        }));

      this.shadowRoot.querySelectorAll('.topic-chip[data-topic]').forEach(chip =>
        chip.addEventListener('click', () => this.handleTopicClick(chip.dataset.topic)));

      const ratingSelect = this.shadowRoot.querySelector('[data-filter="rating"]');
      if (ratingSelect) ratingSelect.addEventListener('change', e => this.handleFilterChange('rating', e.target.value));

      const resetBtn = this.shadowRoot.querySelector('.reset-btn');
      if (resetBtn) resetBtn.addEventListener('click', () => this.resetFilters());

      this.shadowRoot.querySelectorAll('.page-btn[data-page]').forEach(btn =>
        btn.addEventListener('click', () => {
          const p = parseInt(btn.dataset.page);
          if (p >= 1 && p <= pagination.pages) this.handlePageChange(p);
        }));

      this.shadowRoot.querySelectorAll('.dist-row[data-rating]').forEach(row =>
        row.addEventListener('click', () => this.handleFilterChange('rating', row.dataset.rating)));
    }
  }
}

customElements.define('kiyoh-reviews-widget', KiyohReviewsWidget);

export default KiyohReviewsWidget;
